import React from "react";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Calendar as CalendarPicker } from "../ui/calendar";
import { Calendar, CalendarRange } from "lucide-react";
import { DATE_FILTER_OPTIONS, formatDateFilterRange, toYMD } from "../../utils/dateUtils";

// Parse "YYYY-MM-DD" as a LOCAL date (new Date("YYYY-MM-DD") is UTC and
// shifts a day back in western timezones).
const parseYMD = (s) => {
  if (!s) return undefined;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

/**
 * Unified date filter control used across reports.
 *
 * Props:
 *  - value:            string  — the current filter key (e.g. "this_week", "custom")
 *  - onChange:         (v) => void
 *  - customStart:      YYYY-MM-DD string (only relevant when value === "custom")
 *  - customEnd:        YYYY-MM-DD string
 *  - onCustomChange:   ({start, end}) => void
 *  - className:        string — wrapper override
 *  - triggerClassName: string — select trigger override
 *  - dataTestId:       string prefix for test ids
 *
 * Custom range UX: ONE popover with a two-month range calendar. Click the
 * start day, click the end day, press Apply. Nothing is committed (and no
 * report refetch happens) until Apply — month navigation, re-picking days,
 * etc. can never half-apply a range.
 */
export const DateRangeFilter = ({
  value,
  onChange,
  customStart,
  customEnd,
  onCustomChange,
  className = "",
  triggerClassName = "w-[180px] input-glass",
  dataTestId = "date-filter",
}) => {
  const rangeLabel = formatDateFilterRange(value, customStart, customEnd);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [draft, setDraft] = React.useState({ from: undefined, to: undefined });

  const openPicker = (defer = false) => {
    setDraft({ from: parseYMD(customStart), to: parseYMD(customEnd) });
    // When triggered from the Select, the closing dropdown fires an outside-
    // pointer event that would instantly dismiss the popover — defer a tick.
    if (defer) setTimeout(() => setPickerOpen(true), 150);
    else setPickerOpen(true);
  };

  const handleSelectChange = (v) => {
    if (v === "custom") {
      // Don't commit anything yet — just open the range picker. If a range
      // was already applied before, keep showing it until a new Apply.
      if (customStart && customEnd) onChange?.("custom");
      openPicker(true);
      return;
    }
    setPickerOpen(false);
    onChange?.(v);
  };

  const applyDraft = () => {
    const from = draft.from;
    const to = draft.to || draft.from; // single-day range allowed
    if (!from) return;
    onCustomChange?.({ start: toYMD(from), end: toYMD(to) });
    onChange?.("custom");
    setPickerOpen(false);
  };

  const draftLabel = draft.from
    ? `${format(draft.from, "MMM d, yyyy")} → ${draft.to ? format(draft.to, "MMM d, yyyy") : "pick end date"}`
    : "Pick a start date";

  const triggerLabel = customStart && customEnd
    ? `${format(parseYMD(customStart), "MMM d, yyyy")} → ${format(parseYMD(customEnd), "MMM d, yyyy")}`
    : "Pick date range";

  return (
    <div className={`flex flex-col gap-1.5 ${className}`} data-testid={`${dataTestId}-wrapper`}>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger className={triggerClassName} data-testid={`${dataTestId}-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`${dataTestId}-opt-${opt.value}`}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(value === "custom" || pickerOpen) && (
          <Popover open={pickerOpen} onOpenChange={(o) => (o ? openPicker() : setPickerOpen(false))}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="input-glass h-10 font-normal" data-testid={`${dataTestId}-custom-trigger`}>
                <CalendarRange className="w-4 h-4 mr-2 text-muted-foreground" />
                {triggerLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid={`${dataTestId}-custom-popover`}>
              <div className="px-3 pt-3 pb-1">
                <p className="text-xs font-medium text-foreground">Select date range</p>
                <p className="text-[11px] text-muted-foreground" data-testid={`${dataTestId}-draft-label`}>{draftLabel}</p>
              </div>
              <CalendarPicker
                mode="range"
                numberOfMonths={2}
                defaultMonth={draft.from || parseYMD(customStart) || new Date()}
                selected={draft}
                onSelect={(range) => setDraft(range || { from: undefined, to: undefined })}
                data-testid={`${dataTestId}-range-calendar`}
              />
              <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1 border-t border-border">
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground"
                  onClick={() => setDraft({ from: undefined, to: undefined })}
                  data-testid={`${dataTestId}-custom-clear`}>
                  Clear
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setPickerOpen(false)} data-testid={`${dataTestId}-custom-cancel`}>
                    Cancel
                  </Button>
                  <Button size="sm" className="text-xs bg-[#FFCC00] hover:bg-[#e6b800] text-black" disabled={!draft.from}
                    onClick={applyDraft} data-testid={`${dataTestId}-custom-apply`}>
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {rangeLabel && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`${dataTestId}-range-preview`}>
          <Calendar className="w-3 h-3" />
          <span>Showing: <span className="font-medium text-foreground/80">{rangeLabel}</span></span>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
